import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomNavbar from "./CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

const AssignUserPermissions = () => {
  const [userId, setUserId] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [newPermission, setNewPermission] = useState("");
  const [customPermissionsList, setCustomPermissionsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [users, setAllUsers] = useState([]);
  
  // New states for comprehensive management
  const [djangoPermissions, setDjangoPermissions] = useState([]);
  const [availableDjangoPerms, setAvailableDjangoPerms] = useState([]);
  const [selectedDjangoPerms, setSelectedDjangoPerms] = useState([]);
  const [isStaff, setIsStaff] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  // Check if current user is superuser
  useEffect(() => {
    const checkSuperuser = localStorage.getItem("is_superuser") === "true";
    setIsSuperuser(checkSuperuser);
  }, []);

  // Fetch all available permissions (both custom and Django)
  useEffect(() => {
    // Fetch custom permissions (legacy)
    axios
      .get(`${API_BASE_URL}/permissions/`)
      .then((response) => {
        setCustomPermissionsList(response.data);
      })
      .catch((error) => {
        console.error("Error fetching custom permissions:", error);
      });

    // Fetch comprehensive permissions
    axios
      .get(`${API_BASE_URL}/Authentication/manage/available-permissions/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      .then((response) => {
        setAvailableDjangoPerms(response.data.django_permissions || []);
        // Also update custom permissions from new endpoint
        if (response.data.custom_permissions) {
          setCustomPermissionsList(response.data.custom_permissions);
        }
      })
      .catch((error) => {
        console.error("Error fetching available permissions:", error);
      });
  }, []);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(getApiUrl("/Authentication/all_users_list/"), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        
        // Ensure data is an array before setting state
        if (Array.isArray(response.data)) {
          setAllUsers(response.data);
        } else {
          console.error("Users data is not an array:", response.data);
          setAllUsers([]);
          setError("Received invalid users data from server.");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setAllUsers([]);
        if (error.response?.status === 401) {
          setError("Your session has expired. Please log in again.");
        } else {
          setError("Failed to fetch user list.");
        }
      }
    };

    fetchUsers();
  }, []);

  // Fetch user permissions (comprehensive)
  const fetchUserPermissions = async () => {
    if (!userId) {
      setError("Please select a user");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Fetch comprehensive permissions
      const response = await axios.get(
        `${API_BASE_URL}/Authentication/manage/user/${userId}/permissions/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      // Set user info
      setUserInfo(response.data.user);
      setIsStaff(response.data.user.is_staff);

      // Set Django permissions
      setDjangoPermissions(response.data.django_permissions || []);
      setSelectedDjangoPerms(response.data.django_permissions.map((p) => p.id));

      // Set Custom permissions (legacy format)
      const userPermissions = response.data.custom_permissions || [];
      setPermissions(userPermissions);
      setNewPermission(userPermissions.map((perm) => perm.id));

      setSuccess("User permissions loaded successfully");
    } catch (err) {
      console.error("Error fetching user permissions:", err);
      if (err.response?.status === 403) {
        setError("Only superusers can view user permissions");
      } else {
        setError("Failed to load user permissions");
      }
      setUserInfo(null);
      setPermissions([]);
      setDjangoPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Save all permissions (comprehensive)
  const addPermission = () => {
    if (!userId) {
      setError("Please select a user first");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    axios
      .post(
        `${API_BASE_URL}/Authentication/manage/user/${userId}/permissions/update/`,
        {
          is_staff: isStaff,
          django_permissions: selectedDjangoPerms,
          custom_permissions: newPermission,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      )
      .then(() => {
        setPermissions((prev) =>
          customPermissionsList.filter((perm) =>
            newPermission.includes(perm.id)
          )
        );
        setSuccess("Permissions updated successfully!");
        setLoading(false);
        // Refresh permissions
        fetchUserPermissions();
      })
      .catch((err) => {
        console.error("Error updating permissions:", err);
        if (err.response?.status === 403) {
          setError("Only superusers can modify permissions");
        } else {
          setError(err.response?.data?.error || "Error updating permissions");
        }
        setLoading(false);
      });
  };

  // Delete a permission
  const removePermission = (permissionId) => {
    setLoading(true);
    setError("");
    setSuccess("");

    const updatedPermissions = permissions.filter((p) => p.id !== permissionId);

    axios
      .put(`${API_BASE_URL}/user_profile_permissions/${userId}/update/`, {
        user: userId,
        custom_permissions: updatedPermissions,
      })
      .then(() => {
        setPermissions(updatedPermissions);
        setSuccess("Permission removed successfully");
        setLoading(false);
      })
      .catch((err) => {
        setError("Error removing permission");
        setLoading(false);
      });
  };

  // Delete user profile
  const deleteUserProfile = () => {
    // if (!confirm("Are you sure you want to delete this user profile? This action cannot be undone.")) {
    //     return;
    // }

    setLoading(true);
    setError("");
    setSuccess("");

    axios
      .delete(`${API_BASE_URL}/user_profile_permissions/${userId}/delete/`)
      .then(() => {
        setPermissions([]);
        setSuccess("User profile deleted successfully");
        setLoading(false);
      })
      .catch((err) => {
        setError("Error deleting user profile");
        setLoading(false);
      });
  };

  const handleCheckboxChange = (e, permId) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      // Add the permission if checked
      setNewPermission((prev) => [...prev, permId]);
    } else {
      // Remove the permission if unchecked
      setNewPermission((prev) => prev.filter((id) => id !== permId));
    }
  };

  // Handle Django permission toggle
  const handleDjangoPermToggle = (e, permId) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setSelectedDjangoPerms((prev) => [...prev, permId]);
    } else {
      setSelectedDjangoPerms((prev) => prev.filter((id) => id !== permId));
    }
  };

  // Quick role assignment
  const handleQuickAssignRole = async (role) => {
    if (!userId) {
      setError("Please select a user first");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${userId}/assign-role/`,
        { role: role },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      setSuccess(response.data.message);
      // Refresh user permissions
      await fetchUserPermissions();
    } catch (error) {
      console.error("Error assigning role:", error);
      if (error.response?.status === 403) {
        setError("Only superusers can assign roles");
      } else {
        setError(error.response?.data?.error || "Failed to assign role");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <CustomNavbar />
      <div
        className="p-6 bg-white rounded-lg shadow-lg"
        style={{ width: "80vw", margin: "auto" }}
      >
        <h2 className="text-2xl font-bold mb-6 text-blue-500 border-b pb-2">
          User Permissions Management
        </h2>

        {/* User ID Input Section */}
        <div className="mb-6">
          <label
            htmlFor="userId"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            User ID
          </label>
          <div className="flex gap-2" style={{}}>
            <select
              id="userId"
              className="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Select a User</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username}
                </option>
              ))}
            </select>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={fetchUserPermissions}
              disabled={loading || !userId}
            >
              {loading ? "Loading..." : "Fetch Permissions"}
            </button>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError("")}
                  className="text-red-500 hover:text-red-700"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setSuccess("")}
                  className="text-green-500 hover:text-green-700"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Info Card */}
        {userInfo && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-xl font-bold mb-4 text-blue-700">👤 User Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Username</p>
                <p className="font-semibold text-gray-800">{userInfo.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold text-gray-800">{userInfo.email || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  userInfo.role === 'Superuser' ? 'bg-red-100 text-red-800' :
                  userInfo.role === 'Team Leader' ? 'bg-purple-100 text-purple-800' :
                  userInfo.role === 'HR' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {userInfo.role}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  userInfo.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {userInfo.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Staff Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  userInfo.is_staff ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {userInfo.is_staff ? "Yes" : "No"}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Superuser</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  userInfo.is_superuser ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {userInfo.is_superuser ? "Yes" : "No"}
                </span>
              </div>
            </div>

            {/* Quick Role Assignment */}
            <div className="mt-6 pt-6 border-t border-blue-200">
              <h4 className="text-md font-semibold mb-3 text-blue-700">⚡ Quick Role Assignment</h4>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => handleQuickAssignRole("hr")}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  👔 Assign HR Role
                </button>
                <button
                  onClick={() => handleQuickAssignRole("team_leader")}
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  👨‍💼 Assign Team Leader Role
                </button>
                <button
                  onClick={() => handleQuickAssignRole("user")}
                  disabled={loading}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  👤 Assign Basic User Role
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Quick assign will automatically set all required permissions for that role
              </p>
            </div>
          </div>
        )}

        {loading && userId ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Staff Status Toggle */}
            {userInfo && (
              <div className="mb-6 bg-gray-50 p-4 rounded-md border border-gray-200">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isStaff}
                    onChange={(e) => setIsStaff(e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Is Staff Member (required for HR and Team Leaders)
                  </span>
                </label>
              </div>
            )}

            {/* Django Permissions Section */}
            {userInfo && availableDjangoPerms.length > 0 && (
              <div className="mb-6 bg-yellow-50 p-4 rounded-md border border-yellow-200">
                <h3 className="text-lg font-semibold mb-2 text-yellow-800">
                  🔑 Django Permissions ({selectedDjangoPerms.length} selected)
                </h3>
                <p className="text-sm text-yellow-700 mb-3">
                  System-level permissions for authentication and authorization
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {availableDjangoPerms.map((perm) => (
                    <label
                      key={perm.id}
                      className="flex items-start p-2 bg-white rounded border border-yellow-200 hover:bg-yellow-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDjangoPerms.includes(perm.id)}
                        onChange={(e) => handleDjangoPermToggle(e, perm.id)}
                        className="mt-1 w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                      />
                      <div className="ml-2">
                        <div className="text-sm font-medium text-gray-800">{perm.name}</div>
                        <div className="text-xs text-gray-600">{perm.codename}</div>
                        <div className="text-xs text-gray-500">{perm.category}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Current Custom Permissions Section */}
            <div className="mb-6 bg-gray-50 p-4 rounded-md border border-gray-200">
              <h3 className="text-lg font-semibold mb-2 text-gray-700">
                📄 Current Custom Permissions
              </h3>
              {permissions.length > 0 ? (
                <ul className="space-y-2">
                  {permissions.map((perm) => (
                    <li
                      key={perm.id}
                      className="flex items-center justify-between bg-white p-3 rounded-md shadow-sm border border-gray-100"
                    >
                      <span className="text-gray-700">{perm.name}</span>
                      <button
                        className="text-red-500 hover:text-red-700 focus:outline-none"
                        onClick={() => removePermission(perm.id)}
                        title="Remove Permission"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : userId ? (
                <p className="text-gray-500 italic">
                  No permissions assigned to this user.
                </p>
              ) : (
                <p className="text-gray-500 italic">
                  Enter a user ID and fetch permissions to see them here.
                </p>
              )}
            </div>

            {/* Add New Custom Permission Section */}
            <div className="mb-6 bg-green-50 p-4 rounded-md border border-green-200">
              <h3 className="text-lg font-semibold mb-2 text-green-800">
                ✨ Custom Permissions ({newPermission.length} selected)
              </h3>
              <p className="text-sm text-green-700 mb-3">
                Application-specific permissions for pages and features
              </p>
              <div
                style={{ display: "flex", gap: "10px 30px", flexWrap: "wrap" }}
              >
                {customPermissionsList
                  .slice() // Create a shallow copy to avoid mutating the original array
                  .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                  .map((perm) => (
                    <div key={perm.id} className="flex items-center">
                      <input
                        type="checkbox"
                        value={perm.id}
                        checked={newPermission.includes(perm.id)} // Pre-check if assigned
                        onChange={(e) => handleCheckboxChange(e, perm.id)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <label className="ml-2 text-sm text-gray-700">{perm.name}</label>
                    </div>
                  ))}
              </div>
            </div>

            {/* Save All Permissions Button */}
            {userInfo && (
              <div className="flex justify-center mb-6">
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 font-semibold text-lg shadow-lg"
                  onClick={addPermission}
                  disabled={loading || !userId}
                >
                  💾 Save All Permissions
                </button>
              </div>
            )}

            {/* Delete User Profile */}
            {userId && (
              <div className="border-t pt-4">
                <button
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                  onClick={deleteUserProfile}
                  disabled={loading}
                >
                  Delete User Profile
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  This action cannot be undone.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssignUserPermissions;
